import { ComponentEntry, GetCatalogMarkdownOptions, PropDef } from './types';
import { CATALOG } from './catalog';

/**
 * Best-effort mapping from a catalog PropDef's free-form `type` string to a JSON Schema
 * fragment. Prop types that don't map to a JSON Schema primitive (e.g. `BoundValue`, inline
 * object-literal shapes like `{ x: number; ... }`) fall back to an unconstrained `{}` schema —
 * still valid JSON Schema, just permissive, since the catalog does not carry a structured type
 * grammar for those shapes.
 */
function propDefToJsonSchema(prop: PropDef): Record<string, unknown> {
  if (prop.enum) {
    return { type: 'string', enum: prop.enum };
  }
  switch (prop.type) {
    case 'string':
      return { type: 'string' };
    case 'boolean':
      return { type: 'boolean' };
    case 'number':
      return { type: 'number' };
    case 'string[]':
      return { type: 'array', items: { type: 'string' } };
    default:
      return {};
  }
}

/**
 * A dotted `PropDef` key (e.g. `scorecardSettings.viewSystemName`) documents one required member
 * of an otherwise-optional nested config prop — the runtime shape is a real nested object
 * (`props.scorecardSettings.viewSystemName`, see `organismAdapters.tsx`'s `ScorecardAdapter`),
 * never a literal key containing a dot. Schema generation has to build the matching nested
 * `{ type: 'object', properties, required }` fragment, or ajv would require a property literally
 * named `"scorecardSettings.viewSystemName"` — which no real document ever has — and every
 * instance of the entry would fail `PRIMITIVE_PROP_SCHEMA_VIOLATION` regardless of correctness.
 */
function buildPropertiesAndRequired(props: Record<string, PropDef>): { properties: Record<string, unknown>; required: string[] } {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  const nestedProperties: Record<string, Record<string, unknown>> = {};
  const nestedRequired: Record<string, string[]> = {};

  for (const [name, prop] of Object.entries(props)) {
    const dotIndex = name.indexOf('.');
    if (dotIndex === -1) {
      properties[name] = { ...propDefToJsonSchema(prop), description: prop.description };
      if (prop.required) required.push(name);
      continue;
    }

    const parent = name.slice(0, dotIndex);
    const child = name.slice(dotIndex + 1);
    (nestedProperties[parent] ??= {})[child] = { ...propDefToJsonSchema(prop), description: prop.description };
    if (prop.required) (nestedRequired[parent] ??= []).push(child);
  }

  for (const [parent, childProperties] of Object.entries(nestedProperties)) {
    properties[parent] = { type: 'object', properties: childProperties, required: nestedRequired[parent] ?? [] };
    // A nested member is required ⇒ the parent object itself must be present to hold it.
    if ((nestedRequired[parent]?.length ?? 0) > 0) required.push(parent);
  }

  return { properties, required };
}

/**
 * A single catalog entry's `{ properties, required }` fragment — exported so callers that need
 * one entry's schema (e.g. the composer Explorer's knob generation for a code-backed component)
 * don't have to re-derive it or fabricate a `CompositionInterface` wrapper around catalog data.
 */
export function componentEntryToJsonSchema(entry: ComponentEntry): Record<string, unknown> {
  const { properties: propProperties, required: propRequired } = buildPropertiesAndRequired(entry.props);

  return {
    type: 'object',
    description: entry.description,
    properties: {
      id: { type: 'string' },
      component: { const: entry.type },
      ...propProperties,
    },
    required: ['id', 'component', ...propRequired],
  };
}

/**
 * Emits the catalog for a given surface as a JSON Schema document, per the v1.0 catalog
 * conventions: one `definitions` entry per component type with a `const` discriminator on
 * `component`, plus a top-level `oneOf` covering every entry.
 */
export function getCatalogJsonSchema(options: GetCatalogMarkdownOptions): Record<string, unknown> {
  const { surface } = options;
  const entries = CATALOG.filter((e) => e.surfaces.includes(surface));

  const definitions: Record<string, unknown> = {};
  for (const entry of entries) {
    definitions[entry.type] = componentEntryToJsonSchema(entry);
  }

  return {
    $schema: 'http://json-schema.org/draft-07/schema#',
    $id: `https://center-platform/catalog/${surface}`,
    title: `Center Platform ${surface} component catalog`,
    definitions,
    oneOf: entries.map((entry) => ({ $ref: `#/definitions/${entry.type}` })),
  };
}
